/**
 * WCC360 v2 — the delight layer.
 *
 * PRESENTATION ONLY. Nothing here is scored, stored, or sent to the engine.
 * It never changes which screen is served, what an option is worth, or any
 * canonical mapping (Spec v1.0 / Engine Manifest v3.4 / Item Bank v0.2 are
 * immutable). It changes only what the learner *feels* while answering:
 * a named guide, chapter-shaped progress, and levels instead of percentages.
 *
 * Rule kept from the constitution: progress is meaningful, never a
 * percentage. Levels and chapter names are derived from the walk position;
 * no number is ever shown.
 */
import type { Band } from "./band";
import { chaptersFor } from "./chapters";

export interface JourneyLevel {
  /** 1..5 — rendered as filled stars, and announced as text for SR users. */
  rank: 1 | 2 | 3 | 4 | 5;
  name: string;
}

const LEVELS: JourneyLevel[] = [
  { rank: 1, name: "Explorer" },
  { rank: 2, name: "Pathfinder" },
  { rank: 3, name: "Future Builder" },
  { rank: 4, name: "Career Architect" },
  { rank: 5, name: "Future Ready" },
];

/** The level a learner has reached at this point of the walk. */
export function levelFor(fraction: number): JourneyLevel {
  const clamped = Math.min(Math.max(fraction, 0), 1);
  const idx = Math.min(LEVELS.length - 1, Math.floor(clamped * LEVELS.length));
  return LEVELS[idx];
}

export const ALL_LEVELS = LEVELS;

/**
 * YoMA — the guide. One warm, curious line per chapter. Never evaluative,
 * never a hint about a "good" answer, and never a result: a teaser that
 * scored the answers would leak the engine and bias what comes next.
 */
export interface YomaLine {
  chapterId: string;
  intro: string;
  nudge: string;
}

const YOMA_LINES: YomaLine[] = [
  {
    chapterId: "starting",
    intro: "Let's start with where you are right now.",
    nudge: "There's no wrong answer here — just what's true for you.",
  },
  {
    chapterId: "how_you_work",
    intro: "Now the interesting part: how you actually like to work.",
    nudge: "Go with your gut. First instinct is usually the honest one.",
  },
  {
    chapterId: "what_youd_carry",
    intro: "This is the bit that tells me the most about you.",
    nudge: "Think about a real day, not a perfect one.",
  },
  {
    chapterId: "where_going",
    intro: "Last stretch — where you're headed.",
    nudge: "Picture it properly. Then pick.",
  },
];

export function yomaFor(chapterId: string | undefined): YomaLine | null {
  if (!chapterId) return null;
  return YOMA_LINES.find((l) => l.chapterId === chapterId) ?? null;
}

/** Chapter position for the journey map: which chapter, and how many. */
export function chapterMap(screenId: string, band: Band) {
  const chapters = chaptersFor(band);
  const index = chapters.findIndex((c) => c.screens.includes(screenId));
  return { chapters, index };
}

/**
 * XP. PRESENTATION ONLY — a flat award per decision made, derived from the
 * learner's position in the served walk. Never stored, never scored, never
 * sent anywhere. It exists so progress feels earned rather than measured.
 */
export const XP_PER_DECISION = 10;

export function xpForPosition(indexInWalk: number): number {
  if (indexInWalk <= 0) return 0;
  return indexInWalk * XP_PER_DECISION;
}

/**
 * The single source of truth for walk XP. Both the runner header and the
 * tests read this, so the number on screen can never drift from the rule:
 * XP_PER_DECISION for every decision already committed.
 *
 * `served` is the post-conditional screen list; `__end_part1__` (the review
 * screen) means every decision is in, so it awards the full walk.
 */
export function xpForWalk(screenId: string, served: readonly string[]): number {
  if (screenId === "__end_part1__") return xpForPosition(served.length);
  if (screenId === "__intro__") return 0;
  const idx = served.indexOf(screenId);
  return xpForPosition(idx < 0 ? 0 : idx);
}
